import React, { useState, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { useFormik } from "formik";
import * as Yup from "yup";
import useCalculateProratedCost from "api/SubscriptionsResource/useCalculateProratedCost";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { FiPlus, FiUsers, FiCalendar, FiDollarSign, FiLock } from "react-icons/fi";
import { FaGlobe, FaPaypal, FaStar, FaTicketAlt, FaChevronDown, FaChevronUp } from "react-icons/fa";
import EcocashPaymentUsd from "./EcocashPaymentUsd";
import EcocashPaymentZwl from "./EcocashPaymentZwl";
import PayPal from "./PayPal";
import StarsPay from "./StarsPay";
import VoucherPayment from "./VoucherPayment";
import { activeCurrencies, activePaymentMethods, getMoneyPrefix } from "utils";
import Toast from "components/Toast";

const AddSeatsModal = ({ visible, onHide, subscription }) => {
	const [numberOfSeats, setNumberOfSeats] = useState(1);
	const [enableCalculation, setEnableCalculation] = useState(false);
	const [showPayment, setShowPayment] = useState(false);
	const [currency, setCurrency] = useState(activeCurrencies.USD.code);
	const [paymentMethod, setPaymentMethod] = useState(null);
	const [showToast, setShowToast] = useState(false);
	const [toastMessage, setToastMessage] = useState("");

	const {
		data: costDetails,
		isLoading: isCalculating,
		refetch,
	} = useCalculateProratedCost(subscription?.id, numberOfSeats, currency, enableCalculation);

	useEffect(() => {
		if (numberOfSeats > 0 && subscription?.id) {
			setEnableCalculation(true);
			refetch();
		}
	}, [numberOfSeats, subscription?.id, currency, refetch]);

	// Filter payment methods based on selected currency
	const paymentOptionsButtons = React.useMemo(() => {
		const allPaymentOptionsButtons = [
			{
				paymentMethod: activePaymentMethods.ecocashUsd.name,
				label: activePaymentMethods.ecocashUsd.label,
				icon: <FaGlobe size={20} className="opacity-80" />,
				supportedCurrencies: [activeCurrencies.USD.code],
				processingTime: "2-3 mins",
			},
			{
				paymentMethod: activePaymentMethods.ecocashZwl.name,
				label: activePaymentMethods.ecocashZwl.label,
				icon: <FaGlobe size={20} className="opacity-80" />,
				supportedCurrencies: [activeCurrencies.ZWL.code],
				processingTime: "2-3 mins",
			},
			{
				paymentMethod: activePaymentMethods.paypal.name,
				label: activePaymentMethods.paypal.label,
				icon: <FaPaypal size={20} className="opacity-80" />,
				supportedCurrencies: [activeCurrencies.USD.code],
				processingTime: "Instant",
			},
			{
				paymentMethod: activePaymentMethods.stars.name,
				label: activePaymentMethods.stars.label,
				icon: <FaStar size={20} className="opacity-80" />,
				supportedCurrencies: [activeCurrencies.ZWL.code],
				processingTime: "Instant",
			},
			{
				paymentMethod: activePaymentMethods.voucher.name,
				label: activePaymentMethods.voucher.label,
				icon: <FaTicketAlt size={20} className="opacity-80" />,
				supportedCurrencies: [activeCurrencies.USD.code, activeCurrencies.ZWL.code],
				processingTime: "Instant",
			},
		];

		return allPaymentOptionsButtons.filter((option) =>
			option.supportedCurrencies.includes(currency)
		);
	}, [currency]);

	const formik = useFormik({
		initialValues: {
			additional_seats: 1,
		},
		validationSchema: Yup.object({
			additional_seats: Yup.number().min(1, "Must add at least 1 seat").required("Required"),
		}),
		onSubmit: (values) => {
			if (!paymentMethod) {
				setToastMessage("Please select a payment method");
				setShowToast(true);
				return;
			}
			setShowPayment(true);
		},
	});

	const handleClose = () => {
		setShowPayment(false);
		setNumberOfSeats(1);
		formik.resetForm();
		onHide();
	};

	const handleSuccess = () => {
		handleClose();
	};

	return (
		<Dialog
			header="Add seats/students to subscription"
			visible={visible}
			style={{ width: "90vw", maxWidth: "600px" }}
			onHide={handleClose}
			draggable={false}
			className="p-0 font-sans"
		>
			<div className="space-y-6 pt-2 ">
				{/* Current Subscription Info */}
				{subscription && (
					<div className="bg-gradient-to-r from-akgreen to-akgreener rounded-lg p-4 text-white">
						<h3 className="font-semibold text-lg mb-2">{subscription.name}</h3>
						<div className="grid grid-cols-2 gap-4 text-sm">
							<div>
								<div className="opacity-90">Current Seats</div>
								<div className="font-bold text-xl">
									{costDetails?.data?.current_seats || subscription.pivot?.number_of_seats || 1}
								</div>
							</div>
							<div>
								<div className="opacity-90">Expires</div>
								<div className="font-semibold">
									{new Date(
										subscription.expiry_date || subscription.pivot?.expiry_date
									).toLocaleDateString()}
								</div>
							</div>
						</div>
					</div>
				)}

				{!showPayment ? (
					<form onSubmit={formik.handleSubmit} className="space-y-6">
						{/* Add Seats Input */}
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-2">
								<FiUsers className="inline mr-2" />
								Number of Additional Seats
							</label>
							<div className="flex items-center gap-3">
								<button
									type="button"
									onClick={() => {
										const newValue = Math.max(1, numberOfSeats - 1);
										setNumberOfSeats(newValue);
										formik.setFieldValue("additional_seats", newValue);
									}}
									className="w-10 h-10 rounded-lg bg-gray-200 hover:bg-gray-300 flex items-center justify-center text-xl font-bold"
								>
									-
								</button>
								<input
									type="number"
									min="1"
									value={numberOfSeats}
									onChange={(e) => {
										const value = Math.max(1, parseInt(e.target.value) || 1);
										setNumberOfSeats(value);
										formik.setFieldValue("additional_seats", value);
									}}
									className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-center text-2xl font-bold focus:ring-2 focus:ring-akgreen focus:border-transparent"
								/>
								<button
									type="button"
									onClick={() => {
										const newValue = numberOfSeats + 1;
										setNumberOfSeats(newValue);
										formik.setFieldValue("additional_seats", newValue);
									}}
									className="w-10 h-10 rounded-lg bg-akgreen hover:bg-akgreener text-white flex items-center justify-center text-xl font-bold"
								>
									+
								</button>
							</div>
							{formik.errors.additional_seats && formik.touched.additional_seats && (
								<p className="mt-1 text-sm text-red-600">{formik.errors.additional_seats}</p>
							)}
						</div>

						{/* Cost Breakdown */}
						{isCalculating ? (
							<div className="flex justify-center py-8">
								<AiOutlineLoading3Quarters className="animate-spin text-akgreen" size={40} />
							</div>
						) : (
							costDetails?.data && (
								<div className="bg-gray-50 rounded-lg p-4 space-y-3">
									<h4 className="font-semibold text-gray-900 mb-3">Cost Breakdown</h4>

									<div className="flex justify-between text-sm">
										<span className="text-gray-600">
											<FiDollarSign className="inline" /> Price per seat
										</span>
										<span className="font-medium">
											{getMoneyPrefix(currency)} {costDetails.data.price_per_seat.toFixed(2)}
										</span>
									</div>

									<div className="flex justify-between text-sm">
										<span className="text-gray-600">
											<FiUsers className="inline" /> Additional seats
										</span>
										<span className="font-medium">{costDetails.data.additional_seats}</span>
									</div>

									<div className="flex justify-between text-sm">
										<span className="text-gray-600">
											<FiCalendar className="inline" /> Remaining days
										</span>
										<span className="font-medium">
											{costDetails.data.remaining_days} of {costDetails.data.total_days} days
										</span>
									</div>

									<div className="border-t border-gray-300 my-2"></div>

									<div className="flex justify-between items-center">
										<span className="font-semibold text-gray-900">Prorated Total</span>
										<span className="text-2xl font-bold text-akgreen">
											{getMoneyPrefix(currency)} {costDetails.data.prorated_cost.toFixed(2)}
										</span>
									</div>

									<div className="text-xs text-gray-500 mt-2">
										<strong>Note:</strong> You're paying only for the remaining{" "}
										{costDetails.data.remaining_days} days of your subscription period.
									</div>
								</div>
							)
						)}

						{/* Currency Selection */}
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-3">
								Select Currency
							</label>
							<div className="grid grid-cols-2 gap-3">
								<button
									type="button"
									onClick={() => {
										setCurrency(activeCurrencies.USD.code);
										setPaymentMethod(null);
									}}
									className={`py-3 px-4 rounded-lg font-semibold transition-all duration-200 text-center ${
										currency === activeCurrencies.USD.code
											? "bg-akgreen text-white shadow-md transform scale-105"
											: "bg-white text-gray-700 hover:bg-gray-100 border border-gray-200 hover:scale-105"
									}`}
								>
									<div className="flex items-center justify-center gap-2">
										{/* <span className="text-lg">{activeCurrencies.USD.symbol}</span> */}
										<span>{activeCurrencies.USD.label}</span>
									</div>
								</button>
								<button
									type="button"
									onClick={() => {
										setCurrency(activeCurrencies.ZWL.code);
										setPaymentMethod(null);
									}}
									className={`py-3 px-4 rounded-lg font-semibold transition-all duration-200 text-center ${
										currency === activeCurrencies.ZWL.code
											? "bg-akgreen text-white shadow-md transform scale-105"
											: "bg-white text-gray-700 hover:bg-gray-100 border border-gray-200 hover:scale-105"
									}`}
								>
									<div className="flex items-center justify-center gap-2">
										<span className="text-lg">{activeCurrencies.ZWL.label}</span>
									</div>
								</button>
							</div>
						</div>

						{/* Trust Indicator */}
						<div className="flex items-center justify-center gap-2 text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
							<FiLock className="text-akgreen" />
							<span>Your payment is secure</span>
						</div>

						{/* Payment Method Selection */}
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-3">
								Select Payment Method
							</label>
							<div className="space-y-3">
								{paymentOptionsButtons.map((button) => (
									<div
										key={button.paymentMethod}
										className={`border rounded-xl overflow-hidden transition-all duration-300 ${
											paymentMethod === button.paymentMethod
												? "border-akgreen shadow-md"
												: "border-gray-200"
										}`}
									>
										<button
											type="button"
											className={`w-full flex items-center justify-between p-4 ${
												paymentMethod === button.paymentMethod
													? "bg-akgreen bg-opacity-10"
													: "bg-gray-50 hover:bg-gray-100"
											}`}
											onClick={() =>
												setPaymentMethod(
													paymentMethod === button.paymentMethod ? null : button.paymentMethod
												)
											}
										>
											<div className="flex items-center gap-3 flex-1">
												<span className="text-xl">{button.icon}</span>
												<div className="flex flex-col items-start">
													<span className="font-semibold">{button.label}</span>
													<span className="text-xs text-gray-500">{button.processingTime}</span>
												</div>
											</div>
											{paymentMethod === button.paymentMethod ? (
												<FaChevronUp className="text-akgreen flex-shrink-0" />
											) : (
												<FaChevronDown className="text-gray-400 flex-shrink-0" />
											)}
										</button>
									</div>
								))}
							</div>
						</div>

						{/* Submit Button */}
						<div className="flex gap-3 pt-4">
							<button
								type="button"
								onClick={handleClose}
								className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
							>
								Cancel
							</button>
							<button
								type="submit"
								disabled={!costDetails?.data || isCalculating}
								className="flex-1 px-6 py-3 bg-gradient-to-r from-akgreen to-akgreener text-white font-semibold rounded-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
							>
								<FiPlus size={20} />
								Proceed to Payment
							</button>
						</div>
					</form>
				) : (
					<div className="space-y-4">
						{/* Payment Components */}
						{paymentMethod === activePaymentMethods.ecocashUsd.name && (
							<EcocashPaymentUsd
								subscriptionId={subscription?.id}
								additionalSeats={numberOfSeats}
								totalAmount={costDetails?.data?.prorated_cost}
								onSuccess={handleSuccess}
								currency={currency}
							/>
						)}

						{paymentMethod === activePaymentMethods.ecocashZwl.name && (
							<EcocashPaymentZwl
								subscriptionId={subscription?.id}
								additionalSeats={numberOfSeats}
								totalAmount={costDetails?.data?.prorated_cost}
								onSuccess={handleSuccess}
								currency={currency}
							/>
						)}

						{paymentMethod === activePaymentMethods.paypal.name && (
							<PayPal
								subscriptionId={subscription?.id}
								additionalSeats={numberOfSeats}
								totalAmount={costDetails?.data?.prorated_cost}
								onSuccess={handleSuccess}
								currency={currency}
							/>
						)}

						{paymentMethod === activePaymentMethods.stars.name && (
							<StarsPay
								subscriptionId={subscription?.id}
								additionalSeats={numberOfSeats}
								totalAmount={costDetails?.data?.prorated_cost}
								onSuccess={handleSuccess}
								currency={currency}
							/>
						)}

						{paymentMethod === activePaymentMethods.voucher.name && (
							<VoucherPayment
								subscriptionId={subscription?.id}
								additionalSeats={numberOfSeats}
								totalAmount={costDetails?.data?.prorated_cost}
								onSuccess={handleSuccess}
								currency={currency}
							/>
						)}

						<button
							onClick={() => setShowPayment(false)}
							className="w-full px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
						>
							Back
						</button>
					</div>
				)}

				{/* Toast Notification */}
				{showToast && (
					<Toast
						message={toastMessage}
						type="warning"
						onClose={() => setShowToast(false)}
						duration={3000}
					/>
				)}
			</div>
		</Dialog>
	);
};

export default AddSeatsModal;
